import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Form, Input, Button, Modal, Select, DatePicker, TimePicker, Row, Col, InputNumber, message, Tooltip } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsersByOrganisation, clearOrganisationUsers } from '../../redux/userOrganisationsSlice';
import { fetchUsers } from '../../redux/userOrganisationsSlice';
import dayjs from 'dayjs';
import ConfirmModal from '../ConfirmModal';

const { Option } = Select;
const { TextArea } = Input;

const workSchedules = [
  '6/1', '5/2', '4/4', '4/3', '4/2', '3/3', '2/2', '1/3', '1/2',
  'По выходным', 'Свободный', 'Другое'
];

const vacancyReasons = [
  'Новая вакансия',
  'Внутренний перевод',
  'Декрет',
  'Замена'
];



const CreateModal = ({ visible, onCancel, onSubmit }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [draftLoading, setDraftLoading] = useState(false);
  const [isDraftSaved, setIsDraftSaved] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const dispatch = useDispatch();

  const initialValuesRef = useRef({});
  const organisations = useSelector(state => state.organisationDepartment.organisations);
  const { list: organisationUsers, organisationUsers: orgUsers, isLoading: usersLoading } = useSelector(state => state.userOrganisations);
  const users = useSelector(state => state.userOrganisations.list);
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [selectedOrganisation, setSelectedOrganisation] = useState(null);
  const { user } = useSelector((state) => state.auth);
  const authUser = useSelector((state) => state.auth.user);
  const [selectedApprovers, setSelectedApprovers] = useState([]);

  const businessTripsRequired = Form.useWatch('business_trips_required', form);
  const relocationWillingness = Form.useWatch('relocation_willingness', form);
  const communicationCompensation = Form.useWatch('communication_compensation', form);
  const carCompensation = Form.useWatch('car_compensation', form);
  const isMaterialResponsible = Form.useWatch('is_material_responsible', form);
  const driverLicenseRequired = Form.useWatch('driver_license_required', form);
  const ownCarRequired = Form.useWatch('own_car_required', form);

  const handleCheckboxToggle = (fieldName) => {
    const currentValue = form.getFieldValue(fieldName) || false;
    form.setFieldsValue({ [fieldName]: !currentValue });
  };

  const getValidationRules = useCallback((fieldName, isDraft = false) => {

    const draftRequiredFields = {
      company: { required: true, message: 'Выберите компанию' },
      approver_hr: { required: true, message: 'Выберите согласующего HR' },
      job_title: { required: true, message: 'Укажите название должности' }
    };

    if (isDraft) {
      return draftRequiredFields[fieldName] ? [draftRequiredFields[fieldName]] : [];
    }

    const requiredFields = {
      company: { required: true, message: 'Выберите компанию' },
      approver_hr: { required: true, message: 'Выберите согласующего HR' },
      submission_date: { required: true, message: 'Укажите дату подачи заявки' },
      department: { required: true, message: 'Выберите отдел' },
      city: { required: true, message: 'Укажите город' },
      planned_start_date: { required: true, message: 'Укажите планируемую дату выхода сотрудника на работу' },
      author_name: { required: true, message: 'Укажите ФИО автора заявки' },
      author_position: { required: true, message: 'Укажите должность автора заявки' },
      job_title: { required: true, message: 'Укажите название должности' },
      count: { required: true, message: 'Укажите количество позиций' },
      rate: { required: true, message: 'Выберите ставку' },
      vacancy_reason: { required: true, message: 'Выберите причину возникновения вакансии' },
      supervisor: { required: true, message: 'Укажите начальника' },
      subordinates_count: { required: true, message: 'Укажите количество подчиненных' },
      is_material_responsible: { required: true, message: 'Укажите материально ответственное лицо' },
      tasks: { required: true, message: 'Опишите задачи' },
      expected_results: { required: true, message: 'Опишите ожидаемые результаты' },
      functional_responsibilities: { required: true, message: 'Опишите функциональные обязанности' },
      work_schedule: { required: true, message: 'Выберите график работы' },
      work_start_time: { required: true, message: 'Укажите время начала работы' },
      work_end_time: { required: true, message: 'Укажите время окончания работы' },
      business_trips_details: {
        required: false,
        message: 'Укажите детали командировок'
      },
      relocation_willingness: { required: true, message: 'Укажите готовность к переезду' },
      salary_structure: { required: true, message: 'Опишите структуру заработной платы' },
      probation_salary_min: { required: true, message: 'Укажите минимальную зарплату на период испытательного срока' },
      probation_salary_max: { required: true, message: 'Укажите максимальную зарплату на период испытательного срока' },
      regular_salary_min: { required: true, message: 'Укажите минимальную зарплату после испытательного срока' },
      regular_salary_max: { required: true, message: 'Укажите максимальную зарплату после испытательного срока' },
      work_address: { required: true, message: 'Укажите адрес рабочего места' }
    };

    return requiredFields[fieldName] ? [requiredFields[fieldName]] : [];
  }, []);

  const updateFormValidation = useCallback((isDraft) => {
    if (isDraft) {

      const draftFields = ['company', 'approver_hr', 'job_title'];
      draftFields.forEach(fieldName => {
        const rules = getValidationRules(fieldName, isDraft);
        form.setFields([{ name: fieldName, rules }]);
      });
    } else {

      const fields = [
        'company', 'approver_hr', 'submission_date', 'department', 'city',
        'planned_start_date', 'author_name', 'author_position', 'job_title',
        'count', 'rate', 'vacancy_reason', 'supervisor', 'subordinates_count',
        'is_material_responsible', 'tasks', 'expected_results', 'functional_responsibilities',
        'work_schedule', 'work_start_time', 'work_end_time', 'business_trips_details',
        'relocation_willingness', 'salary_structure', 'probation_salary_min',
        'probation_salary_max', 'regular_salary_min', 'regular_salary_max', 'work_address'
      ];

      fields.forEach(fieldName => {
        const rules = getValidationRules(fieldName, isDraft);
        form.setFields([{ name: fieldName, rules }]);
      });
    }
  }, [form, getValidationRules]);


  const checkForChanges = useCallback(() => {
    const currentValues = form.getFieldsValue(true);
    return JSON.stringify(currentValues) !== JSON.stringify(initialValuesRef.current);
  }, [form]);


  const handleFormChange = useCallback(() => {
    setHasUnsavedChanges(checkForChanges());
  }, [checkForChanges]);

  useEffect(() => {
    if (visible) {
      form.resetFields();
      setIsDraftSaved(false);
      setSelectedApprovers([]);
      setSelectedOrganisation(null);
      setHasUnsavedChanges(false);
      dispatch(clearOrganisationUsers());


      setTimeout(() => {

        let firstName = '';
        let lastName = '';

        if (authUser && authUser.firstName && authUser.lastName) {
          firstName = authUser.firstName;
          lastName = authUser.lastName;
        } else if (user && user.firstName && user.lastName) {
          firstName = user.firstName;
          lastName = user.lastName;
        } else {

          firstName = localStorage.getItem('first_name') || '';
          lastName = localStorage.getItem('last_name') || '';
        }

        const authorName = `${firstName} ${lastName}`.trim();

        const defaultValues = {
          submission_date: dayjs(),
          approver_hr: 7, // Анастасия Матюлина - принудительно для всех заявок
        };

        if (authorName) {
          defaultValues.author_name = authorName;
        }

        form.setFieldsValue(defaultValues);


        initialValuesRef.current = form.getFieldsValue(true);
      }, 0);

      // organisations уже загружены в Layout из auth.userOrganisations
      if (!users || users.length === 0) {
        dispatch(fetchUsers({ skip: 0, limit: 20 }));
      }

      updateFormValidation(false);
    }
  }, [visible, dispatch, form, organisations, users, updateFormValidation, authUser, user]);

  // Устанавливаем approver_hr = 7 после загрузки пользователей, если значение еще не установлено
  useEffect(() => {
    if (visible && users && users.length > 0) {
      const currentApproverHr = form.getFieldValue('approver_hr');
      if (!currentApproverHr || currentApproverHr !== 7) {
        form.setFieldsValue({ approver_hr: 7 });
      }
    }
  }, [visible, users, form]);

  useEffect(() => {
    if (visible) {
      const companyId = form.getFieldValue('company');
      if (companyId) {
        setSelectedOrganisation(companyId);
        dispatch(fetchUsersByOrganisation(companyId));
      }
    }
  }, [visible, organisations, form, dispatch]);

  useEffect(() => {
    if (selectedOrganisation) {
      const org = organisations.find(o => o.organisation_id === selectedOrganisation);
      setFilteredDepartments(org?.departments || []);
    } else {
      setFilteredDepartments([]);
    }
  }, [selectedOrganisation, organisations]);

  const handleApproverChange = (value, index) => {
    const newSelectedApprovers = [...selectedApprovers];
    newSelectedApprovers[index] = value;
    setSelectedApprovers(newSelectedApprovers);
  };

  const handleHRApproverChange = (value) => {
    const newSelectedApprovers = [...selectedApprovers];
    newSelectedApprovers['hr'] = value;
    setSelectedApprovers(newSelectedApprovers);
  };

  const filterApprovers = (userId, isHR = false) => {
    const currentUsers = selectedOrganisation && orgUsers.length > 0 ? orgUsers : users;

    return currentUsers.filter(user => {
      if (isHR) {
        return !selectedApprovers.some(id => id !== 'hr' && id === user.id);
      }
      return (
        (!selectedApprovers.includes(user.id) &&
          user.id !== form.getFieldValue('approver_hr')) ||
        user.id === userId
      );
    });
  };

  const handleCompanyChange = async (orgId) => {
    setSelectedOrganisation(orgId);
    form.setFieldsValue({ department: undefined });
    dispatch(clearOrganisationUsers());

    if (orgId) {
      dispatch(fetchUsersByOrganisation(orgId));
    }
  };

  const handleSubmit = async (isTemplate = false) => {
    try {
      setLoading(true);
      updateFormValidation(isTemplate);

      let values;

      if (isTemplate) {

        const draftRequiredFields = ['company', 'approver_hr', 'job_title'];
        await form.validateFields(draftRequiredFields);
        values = form.getFieldsValue();
      } else {

        values = await form.validateFields();
      }

      const approversManagement = values.approver_management || [];
      const approverHr = values.approver_hr;

      const movements = [
        //   ...approversManagement.map(approverId => (approverId)),
        //   approverHr
        // ];
        ...approversManagement.filter(id => id != null),
        ...(approverHr ? [approverHr] : [])
      ].filter(id => id != null);

      const submissionDate = values.submission_date ? dayjs(values.submission_date).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');

      const data = {
        submission_date: submissionDate,
        organisation: values.company,
        department: values.department,
        city: values.city,
        planned_start_date: values.planned_start_date,
        author: Number(user.id),
        author_position: values.author_position,
        is_template: isTemplate,
        status: 'pending',
        job_title: values.job_title,
        positions_count: values.count || 1,
        rate: values.rate,
        vacancy_reason: values.vacancy_reason,
        supervisors: values.supervisor,
        subordinates_count: values.subordinates_count ? String(values.subordinates_count) : '0',
        is_material_responsible: values.is_material_responsible || false,
        tasks: values.tasks,
        expected_results: values.expected_results,
        functional_responsibilities: values.responsibilities,
        reporting_details: values.reporting,
        work_schedule: values.work_schedule,
        work_start_time: values.work_start_time ? values.work_start_time.format('HH:mm') : '',
        work_end_time: values.work_end_time ? values.work_end_time.format('HH:mm') : '',
        field_vs_office: values.work_location_ratio,
        requires_travel: values.business_trips_required || false,
        travel_details: values.business_trips_details,
        requires_relocation: values.relocation_willingness || false,
        salary_structure: values.salary_structure,
        trial_salary: values.probation_salary_min !== undefined && values.probation_salary_max !== undefined ? `${values.probation_salary_min} - ${values.probation_salary_max}` : '',
        post_trial_salary: values.regular_salary_min !== undefined && values.regular_salary_max !== undefined ? `${values.regular_salary_min} - ${values.regular_salary_max}` : '',
        compensate_phone: values.communication_compensation || false,
        compensate_car: values.car_compensation || false,
        workplace_address: values.work_address,
        gender: values.gender,
        age: values.age,
        marital_status: values.marital_status || [],
        requires_license: values.driver_license_required || false,
        license_categories: values.driver_license_categories || [],
        requires_car: values.own_car_required || false,
        education_levels: values.education || [],
        computer_skills_level: values.computer_skills,
        skills_1c: values.skills_1c,
        software_skills: (values.software_knowledge || [])
          .filter(sk => sk && sk.program && sk.program.trim() !== '')
          .map(sk => ({
            program: sk.program.trim(),
            level: sk.level || 'Не требуется'
          })),
        language_skills: (values.language_knowledge || [])
          .filter(lang => lang && lang.language && lang.language.trim() !== '')
          .map(lang => ({
            language: lang.language.trim(),
            level: lang.level || 'Не требуется'
          })),
        experience_description: values.work_experience,
        personal_traits: values.personal_qualities,
        professional_skills: values.professional_skills,
        preferred_companies: values.preferred_companies,
        unwanted_companies: values.undesired_companies,
        other_conditions: values.other_conditions,
        movements: movements
      };

      await onSubmit(data);

      setHasUnsavedChanges(false);
      initialValuesRef.current = form.getFieldsValue(true);

    } catch (error) {
      console.error('Ошибка валидации формы:', error);
      if (error?.errorFields?.length > 0) {
        form.scrollToField(error.errorFields[0].name, {
          behavior: 'smooth',
          block: 'center',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    try {
      setDraftLoading(true);
      await handleSubmit(true);
      setIsDraftSaved(true);
      message.success('Черновик сохранен успешно');
    } catch (error) {
      console.error('Ошибка при сохранении черновика:', error);

      if (error?.errorFields?.length > 0) {
        form.scrollToField(error.errorFields[0].name, {
          behavior: 'smooth',
          block: 'center',
        });
      }
    } finally {
      setDraftLoading(false);
    }
  };

  const handleDeleteDraft = async () => {
    setIsDraftSaved(false);
    form.resetFields();
  };


  const handleCancel = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowConfirm(true);
    } else {
      onCancel();
    }
  }, [hasUnsavedChanges, onCancel]);

  const handleConfirmClose = useCallback((shouldClose) => {
    if (shouldClose) {
      onCancel();
    }
    setShowConfirm(false);
  }, [onCancel]);

  const footerButtons = [
    <Tooltip key="back" title="Закрыть модальное окно без сохранения изменений">
      <Button onClick={handleCancel}>
        Закрыть
      </Button>
    </Tooltip>,


    <Tooltip key="draft" title="Сохранить заявку как черновик для последующего редактирования">
      <Button
        type="default"
        loading={draftLoading}
        onClick={handleSaveDraft}
      >
        Сохранить как черновик
      </Button>
    </Tooltip>,

    // ) : (
    //   <Button
    //     key="delete"
    //     type="default"
    //     danger
    //     onClick={handleDeleteDraft}
    //   >
    //     Удалить
    //   </Button>
    // ),

    <Tooltip key="submit" title="Создать заявку и отправить на согласование">
      <Button
        type="primary"
        loading={loading}
        onClick={() => handleSubmit(false)}
      >
        Создать заявку
      </Button>
    </Tooltip>
  ];

  return (
    <>
      <Modal
        title="Создание заявки на вакансию"
        open={visible}
        onCancel={handleCancel}
        width={1000}
        footer={footerButtons}
        afterClose={() => {
          form.resetFields();
          setHasUnsavedChanges(false);
        }}
      >
        <Form
          form={form}
          layout="vertical"
          onFieldsChange={handleFormChange}
        >
          {/* Согласование */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Согласование
            </h3>

            <Form.Item name="company" label="Компания" rules={getValidationRules('company', false)}>
              <Select showSearch optionFilterProp="children" placeholder="Выберите компанию из доступных для вас" onChange={handleCompanyChange}>
                <Option value="" disabled>Выберите компанию из доступных для вас</Option>
                {organisations.map(o => (
                  <Option key={o.id} value={o.organisation_id}>{o.name}</Option>
                ))}
              </Select>
            </Form.Item>

            <Form.List name="approver_management">
              {(fields, { add, remove }) => {
                const approvers = form.getFieldValue('approver_management') || [];
                const allFilled = approvers.every(approver => approver !== undefined);

                return (
                  <>
                    {fields.map(({ key, name, ...restField }) => {
                      const fieldValue = form.getFieldValue(['approver_management', name]);
                      return (
                        <Row gutter={8} key={key}>
                          <Col span={22}>
                            <Form.Item
                              {...restField}
                              name={[name]}
                              label={`Согласующий №${name + 1}`}
                              rules={getValidationRules('approver_management', false)}
                            >
                              <Select
                                placeholder="Выберите согласующего"
                                onChange={(value) => handleApproverChange(value, name)}
                                showSearch
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                                loading={usersLoading}
                              >
                                {filterApprovers(fieldValue).map(u => (
                                  <Option key={u.id} value={u.id}>
                                    {u.last_name} {u.first_name} {u.middle_name || ''}
                                  </Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col span={2} style={{ marginTop: '8px', display: 'flex', alignItems: 'center' }}>
                            <Button danger onClick={() => {
                              const newSelectedApprovers = [...selectedApprovers];
                              newSelectedApprovers.splice(name, 1);
                              setSelectedApprovers(newSelectedApprovers);
                              remove(name);
                            }}>
                              Удалить
                            </Button>
                          </Col>
                        </Row>
                      );
                    })}
                    {users.length > 0 && (
                      <Form.Item>
                        <Button
                          type="dashed"
                          onClick={() => {
                            if (allFilled || approvers.length === 0) {
                              add();
                            } else {
                              message.error('Заполните текущие поля согласующих перед добавлением нового');
                            }
                          }}
                          disabled={filterApprovers().length === 0}
                        >
                          Добавить согласующего
                        </Button>
                      </Form.Item>
                    )}
                  </>
                );
              }}
            </Form.List>

            <Form.Item
              name="approver_hr"
              label="Распределяющий HR"
              rules={getValidationRules('approver_hr', false)}
              initialValue={7}
            >
              <Select
                placeholder="Распределяющий HR"
                onChange={handleHRApproverChange}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                loading={usersLoading}
                disabled={true}
              >
                {filterApprovers(form.getFieldValue('approver_hr'), true).map(u => (
                  <Option key={u.id} value={u.id}>
                    {u.last_name} {u.first_name} {u.middle_name || ''}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          {/* Основная информация */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Основная информация
            </h3>

            <Row gutter={16}>
              <Col span={12}>

                <Form.Item name="job_title" label="Название должности" rules={getValidationRules('job_title', false)}>
                  <Input />
                </Form.Item>

                <Form.Item name="department" label="Отдел" rules={getValidationRules('department', false)}>
                  <Select placeholder="Выберите отдел">
                    {filteredDepartments.map(d => (
                      <Option key={d.id} value={d.id}>{d.name}</Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item name="city" label="Город" rules={getValidationRules('city', false)}>
                  <Input />
                </Form.Item>

                <Form.Item name="submission_date" label="Дата подачи заявки" rules={getValidationRules('submission_date', false)}>
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item name="author_name" label="Автор заявки (ФИО)" rules={getValidationRules('author_name', false)}>
                  <Input readOnly />
                </Form.Item>

                <Form.Item name="author_position" label="Автор заявки (должность)" rules={getValidationRules('author_position', false)}>
                  <Input />
                </Form.Item>

                <Form.Item name="planned_start_date" label="Планируемая дата выхода сотрудника на работу" rules={getValidationRules('planned_start_date', false)}>
                  <Input placeholder="Можно указать дату, месяц или период (например: 01.09.2023, сентябрь 2023, 3-й квартал 2023)" />
                </Form.Item>

                <Form.Item name="count" label="Количество позиций" rules={getValidationRules('count', false)}>
                  <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Информация о вакансии */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Информация о вакансии
            </h3>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="rate" label="Ставка" rules={getValidationRules('rate', false)}>
                  <Select placeholder="Выберите ставку">
                    {['0.25', '0.5', '0.75', '1', '1.25', '1.5', '1.75', '2'].map(value => (
                      <Option key={value} value={value}>
                        {value}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item name="supervisor" label="Начальник" rules={getValidationRules('supervisor', false)}>
                  <Input />
                </Form.Item>

                <Form.Item name="is_material_responsible" valuePropName="checked" style={{ display: 'none' }}>
                  <Input type="hidden" />
                </Form.Item>
                <Form.Item label="Материально ответственное лицо">
                  <Button
                    type={isMaterialResponsible ? "primary" : "default"}
                    onClick={() => handleCheckboxToggle('is_material_responsible')}
                    style={{
                      width: '100%',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: isMaterialResponsible ? 'bold' : 'normal'
                    }}
                  >
                    {isMaterialResponsible ? '✓ Материально ответственное лицо' : 'Материально ответственное лицо'}
                  </Button>
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item name="vacancy_reason" label="Причина возникновения вакансии" rules={getValidationRules('vacancy_reason', false)}>
                  <Select>
                    {vacancyReasons.map(reason => (
                      <Option key={reason} value={reason}>{reason}</Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item name="subordinates_count" label="Подчиненные (количество)" rules={getValidationRules('subordinates_count', false)}>
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>

              </Col>
            </Row>
          </div>

          {/* Должностные обязанности */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Должностные обязанности
            </h3>

            <Form.Item name="tasks" label="Задачи" rules={getValidationRules('tasks', false)}>
              <TextArea rows={3} placeholder="Опишите задачи" />
            </Form.Item>

            <Form.Item name="responsibilities" label="Функциональные обязанности" rules={getValidationRules('functional_responsibilities', false)}>
              <TextArea rows={3} placeholder="Опишите функциональные обязанности" />
            </Form.Item>

            <Form.Item name="reporting" label="Отчетность о проделанной работе">
              <TextArea rows={3} placeholder="Опишите отчетность" />
            </Form.Item>

            <Form.Item name="expected_results" label="Ожидаемые результаты" rules={getValidationRules('expected_results', false)}>
              <TextArea rows={3} />
            </Form.Item>
          </div>

          {/* Условия работы */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Условия работы
            </h3>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="work_schedule" label="График работы" rules={getValidationRules('work_schedule', false)}>
                  <Select placeholder="Выберите график работы">
                    {workSchedules.map(schedule => (
                      <Option key={schedule} value={schedule}>{schedule}</Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item name="work_start_time" label="Начало работы" rules={getValidationRules('work_start_time', false)}>
                  <TimePicker
                    style={{ width: '100%' }}
                    format="HH:mm"
                    needConfirm={false}
                    placeholder="Выберите время начала работы"
                  />
                </Form.Item>

                <Form.Item name="relocation_willingness" valuePropName="checked" style={{ display: 'none' }}>
                  <Input type="hidden" />
                </Form.Item>

                <Form.Item label="Командировки">
                  <Button
                    type={businessTripsRequired ? "primary" : "default"}
                    onClick={(e) => {
                      e.preventDefault();
                      handleCheckboxToggle('business_trips_required');
                    }}
                    htmlType="button"
                    style={{
                      width: '100%',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: businessTripsRequired ? 'bold' : 'normal'
                    }}
                  >
                    {businessTripsRequired ? '✓ Командировки' : 'Командировки'}
                  </Button>
                </Form.Item>

                {businessTripsRequired && (
                  <Form.Item name="business_trips_details" label="Частота и длительность командировок" rules={getValidationRules('business_trips_details', false, businessTripsRequired)}>
                    <Input placeholder="Например: 2 раза в месяц, 3-5 дней" />
                  </Form.Item>
                )}
              </Col>

              <Col span={12}>
                <Form.Item name="work_location_ratio" label="Соотношение работы: дом/офис">
                  <Input placeholder="Например: 70% офис, 30% дом" />
                </Form.Item>

                <Form.Item name="work_end_time" label="Окончание работы" rules={getValidationRules('work_end_time', false)}>
                  <TimePicker
                    style={{ width: '100%' }}
                    format="HH:mm"
                    needConfirm={false}
                    placeholder="Выберите время окончания работы"
                  />
                </Form.Item>

                <Form.Item name="business_trips_required" valuePropName="checked" style={{ display: 'none' }}>
                  <Input type="hidden" />
                </Form.Item>

                <Form.Item label="Готовность к переезду">
                  <Button
                    type={relocationWillingness ? "primary" : "default"}
                    onClick={() => handleCheckboxToggle('relocation_willingness')}
                    style={{
                      width: '100%',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: relocationWillingness ? 'bold' : 'normal'
                    }}
                  >
                    {relocationWillingness ? '✓ Готовность к переезду' : 'Готовность к переезду'}
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Зарплата */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Зарплата
            </h3>

            <Form.Item name="salary_structure" label="Заработная плата (структура)" rules={getValidationRules('salary_structure', false)}>
              <TextArea rows={2} placeholder="Опишите как формируется заработная плата" />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Зарплата на период испытательного срока (BYN)"
                  required
                  validateStatus={form.getFieldError('probation_salary_min')?.length > 0 || form.getFieldError('probation_salary_max')?.length > 0 ? 'error' : ''}
                  help={form.getFieldError('probation_salary_min')?.[0] || form.getFieldError('probation_salary_max')?.[0]}
                >
                  <Input.Group compact>
                    <Form.Item name="probation_salary_min" noStyle rules={getValidationRules('probation_salary_min', false)}>
                      <InputNumber
                        min={0}
                        placeholder="От"
                        style={{ width: '50%' }}
                        parser={value => value.replace(/\s?BYN/g, '')}
                      />
                    </Form.Item>
                    <Form.Item name="probation_salary_max" noStyle rules={getValidationRules('probation_salary_max', false)}>
                      <InputNumber
                        min={0}
                        placeholder="До"
                        style={{ width: '50%' }}
                        parser={value => value.replace(/\s?BYN/g, '')}
                      />
                    </Form.Item>
                  </Input.Group>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Зарплата после испытательного срока (BYN)"
                  required
                  validateStatus={form.getFieldError('regular_salary_min')?.length > 0 || form.getFieldError('regular_salary_max')?.length > 0 ? 'error' : ''}
                  help={form.getFieldError('regular_salary_min')?.[0] || form.getFieldError('regular_salary_max')?.[0]}
                >
                  <Input.Group compact>
                    <Form.Item name="regular_salary_min" noStyle rules={getValidationRules('regular_salary_min', false)}>
                      <InputNumber
                        min={0}
                        placeholder="От"
                        style={{ width: '50%' }}
                        parser={value => value.replace(/\s?BYN/g, '')}
                      />
                    </Form.Item>
                    <Form.Item name="regular_salary_max" noStyle rules={getValidationRules('regular_salary_max', false)}>
                      <InputNumber
                        min={0}
                        placeholder="До"
                        style={{ width: '50%' }}
                        parser={value => value.replace(/\s?BYN/g, '')}
                      />
                    </Form.Item>
                  </Input.Group>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                {/* Компенсация связи */}
                <Form.Item name="communication_compensation" valuePropName="checked" style={{ display: 'none' }}>
                  <Input type="hidden" />
                </Form.Item>
                <Form.Item label="Компенсация затрат: связь">
                  <Button
                    type={communicationCompensation ? "primary" : "default"}
                    onClick={() => handleCheckboxToggle('communication_compensation')}
                    style={{
                      width: '100%',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: communicationCompensation ? 'bold' : 'normal'
                    }}
                  >
                    {communicationCompensation ? '✓ Компенсация затрат: связь' : 'Компенсация затрат: связь'}
                  </Button>
                </Form.Item>
              </Col>
              <Col span={12}>
                {/* Компенсация автомобиля */}
                <Form.Item name="car_compensation" valuePropName="checked" style={{ display: 'none' }}>
                  <Input type="hidden" />
                </Form.Item>
                <Form.Item label="Компенсация затрат: личный автомобиль">
                  <Button
                    type={carCompensation ? "primary" : "default"}
                    onClick={() => handleCheckboxToggle('car_compensation')}
                    style={{
                      width: '100%',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: carCompensation ? 'bold' : 'normal'
                    }}
                  >
                    {carCompensation ? '✓ Компенсация затрат: личный автомобиль' : 'Компенсация затрат: личный автомобиль'}
                  </Button>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="work_address" label="Адрес рабочего места" rules={getValidationRules('work_address', false)}>
              <Input placeholder="Полный адрес рабочего места" />
            </Form.Item>
          </div>

          {/* Требования к кандидату */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Требования к кандидату
            </h3>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="gender" label="Пол">
                  <Select options={[
                    { value: 'Мужской', label: 'Мужской' },
                    { value: 'Женский', label: 'Женский' },
                    { value: 'Не имеет значения', label: 'Не имеет значения' },
                  ]} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="age" label="Возраст">
                  <Input placeholder="Укажите возраст или диапазон" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="marital_status" label="Семейное положение">
                  <Select
                    mode="multiple"
                    options={[
                      { value: 'Холост/Не замужем', label: 'Холост/Не замужем' },
                      { value: 'Женат/Замужем', label: 'Женат/Замужем' },
                      { value: 'Разведен(а)', label: 'Разведен(а)' },
                      { value: 'Вдовец/Вдова', label: 'Вдовец/Вдова' },
                      { value: 'Не имеет значения', label: 'Не имеет значения' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="education" label="Образование">
                  <Select
                    mode="multiple"
                    options={[
                      'Базовое',
                      'Среднее',
                      'Профессионально-техническое',
                      'Среднее специальное',
                      'Высшее'
                    ].map(ed => ({ value: ed, label: ed }))}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                {/* Водительское удостоверение */}
                <Form.Item name="driver_license_required" valuePropName="checked" style={{ display: 'none' }}>
                  <Input type="hidden" />
                </Form.Item>
                <Form.Item label="Наличие водительского удостоверения">
                  <Button
                    type={driverLicenseRequired ? "primary" : "default"}
                    onClick={() => handleCheckboxToggle('driver_license_required')}
                    style={{
                      width: '100%',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: driverLicenseRequired ? 'bold' : 'normal'
                    }}
                  >
                    {driverLicenseRequired ? '✓ Наличие водительского удостоверения' : 'Наличие водительского удостоверения'}
                  </Button>
                </Form.Item>

                {driverLicenseRequired && (
                  <Form.Item name="driver_license_categories" label="Категории водительских прав">
                    <Select
                      mode="multiple"
                      options={[
                        'АМ', 'А', 'А1', 'В', 'С', 'D', 'ВЕ', 'СЕ', 'DE', 'F', 'I'
                      ].map(cat => ({ value: cat, label: cat }))}
                    />
                  </Form.Item>
                )}
              </Col>
              <Col span={12}>
                <Form.Item name="own_car_required" valuePropName="checked" style={{ display: 'none' }}>
                  <Input type="hidden" />
                </Form.Item>
                <Form.Item label="Наличие личного автомобиля">
                  <Button
                    type={ownCarRequired ? "primary" : "default"}
                    onClick={() => handleCheckboxToggle('own_car_required')}
                    style={{
                      width: '100%',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: ownCarRequired ? 'bold' : 'normal'
                    }}
                  >
                    {ownCarRequired ? '✓ Наличие личного автомобиля' : 'Наличие личного автомобиля'}
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Навыки и опыт */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Навыки и опыт
            </h3>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="computer_skills" label="Уровень владения ПК (Word, Excel и др.)">
                  <Select
                    options={[
                      'Не требуется',
                      'Базовый',
                      'Уверенный',
                      'Продвинутый'
                    ].map(level => ({ value: level, label: level }))}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="skills_1c" label="Навыки в работе с 1С">
                  <Input.TextArea rows={1} />
                </Form.Item>
              </Col>
            </Row>

            <Form.List name="software_knowledge">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Row gutter={8} key={key}>
                      <Col span={12}>
                        <Form.Item {...restField} name={[name, 'program']} label="Программа">
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={10}>
                        <Form.Item {...restField} name={[name, 'level']} label="Уровень владения">
                          <Select options={[
                            'Базовый',
                            'Уверенный',
                            'Продвинутый'
                          ].map(l => ({ value: l, label: l }))} />
                        </Form.Item>
                      </Col>
                      <Col span={2} style={{ marginTop: '6px', display: 'flex', alignItems: 'center' }}>
                        <Button danger onClick={() => remove(name)}>Удалить</Button>
                      </Col>
                    </Row>
                  ))}
                  <Form.Item label="Знание других специализированных программ">
                    <Button type="dashed" onClick={() => add()}>Добавить программу</Button>
                  </Form.Item>
                </>
              )}
            </Form.List>

            <Form.List name="language_knowledge">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Row gutter={8} key={key}>
                      <Col span={12}>
                        <Form.Item {...restField} name={[name, 'language']} label="Язык">
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={10}>
                        <Form.Item {...restField} name={[name, 'level']} label="Уровень владения">
                          <Select options={[
                            'Базовый',
                            'Уверенный',
                            'Продвинутый'
                          ].map(l => ({ value: l, label: l }))} />
                        </Form.Item>
                      </Col>
                      <Col span={2} style={{ marginTop: '6px', display: 'flex', alignItems: 'center' }}>
                        <Button danger onClick={() => remove(name)}>Удалить</Button>
                      </Col>
                    </Row>
                  ))}
                  <Form.Item label="Знание иностранных языков">
                    <Button type="dashed" onClick={() => add()}>Добавить язык</Button>
                  </Form.Item>
                </>
              )}
            </Form.List>

            <Form.Item name="work_experience" label="Опыт работы (область, продолжительность)">
              <TextArea rows={2} />
            </Form.Item>
          </div>

          {/* Личные и профессиональные качества */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Личные и профессиональные качества
            </h3>

            <Form.Item name="personal_qualities" label="Личные качества кандидатов">
              <TextArea rows={3} />
            </Form.Item>

            <Form.Item name="professional_skills" label="Профессиональные навыки кандидатов">
              <TextArea rows={3} />
            </Form.Item>
          </div>

          {/* Дополнительные требования */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Дополнительные требования
            </h3>

            <Form.Item name="preferred_companies" label="Компании, из которых предпочтительно рассматривать кандидатов">
              <TextArea rows={2} />
            </Form.Item>

            <Form.Item name="undesired_companies" label="Компании, из которых нежелательно рассматривать кандидатов">
              <TextArea rows={2} />
            </Form.Item>

            <Form.Item name="other_conditions" label="Другие условия">
              <TextArea rows={3} />
            </Form.Item>
          </div>
        </Form>
      </Modal>

      <ConfirmModal
        visible={showConfirm}
        onConfirm={handleConfirmClose}
        onCancel={() => setShowConfirm(false)}
        title="Подтверждение закрытия"
        message="Данные не будут сохранены. Продолжить?"
        okText="Продолжить"
        cancelText="Отмена"
        type="warning"
      />
    </>
  );
};

export default CreateModal;